/**
 * Toolbar controls nested inside cards: use inner radius (rounded-md) so corners
 * stay concentric with the outer card (rounded-lg).
 *
 * Prefer **canvas** toolbar (Products/Locations/Feedback) by default. Use these
 * tokens with `ListToolbarSurface` when a nested Card toolbar is intentional.
 */
export const listToolbarSearchInputClass =
  'h-10 rounded-md border-border/60 bg-card pl-10 pr-4 text-sm shadow-xs transition-[background-color,box-shadow] placeholder:text-muted-foreground/70 focus-visible:bg-card focus-visible:ring-1 focus-visible:ring-ring dark:bg-input/30';

/** Leads list toolbar — matches LeadsListDesktop search (34px, surface-2). */
export const leadsListSearchInputClass =
  'h-[34px] rounded-[var(--radius-input)] border-[color:var(--surface-border)] bg-[color:var(--surface-2)] py-0 pl-10 pr-3 text-[13px] md:text-[13px] shadow-none transition-[background-color,box-shadow,border-color] placeholder:text-muted-foreground focus-visible:bg-[color:var(--surface-2)] focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0';

/**
 * L8 Mehrfachauswahl action chips — 34px, card on surface-2 header.
 * Labelled chips use horizontal padding; icon-only use fixed width.
 */
export const leadsBulkToolbarButtonClass =
  'inline-flex h-[34px] shrink-0 items-center justify-center gap-[7px] rounded-[var(--radius-input)] border border-[color:var(--surface-border)] bg-card px-[13px] text-[12.5px] font-semibold text-foreground shadow-none transition-colors hover:bg-muted/40 disabled:opacity-50';

export const leadsBulkToolbarIconButtonClass =
  'inline-flex size-[34px] shrink-0 items-center justify-center rounded-[var(--radius-input)] border border-[color:var(--surface-border)] bg-card p-0 text-foreground shadow-none transition-colors hover:bg-muted/40 disabled:opacity-50';

export const listToolbarFilterButtonClass =
  'h-10 min-h-10 shrink-0 gap-2 rounded-md border-border/60 bg-card px-4 py-0 text-sm font-medium shadow-xs ring-offset-background transition-[background-color,box-shadow] focus-visible:bg-card focus-visible:ring-1 focus-visible:ring-ring dark:bg-input/30';

export const listToolbarFilterChipClass =
  'inline-flex max-w-full items-center rounded-md border px-2.5 py-1 text-xs font-medium transition-colors';

/** Fields nested inside cards (read-only or editable). */
export const nestedCardFieldClass =
  'rounded-md border border-border bg-card text-foreground/80 shadow-[inset_0_1px_3px_0_color-mix(in_oklab,#000_7%,transparent)]';

/** Outline action buttons nested inside cards — subtle color fade on hover only. */
export const nestedCardOutlineButtonClass =
  'rounded-md border border-border bg-card text-foreground font-medium transition-colors duration-150 shadow-[inset_0_1px_0_color-mix(in_oklab,var(--foreground)_14%,transparent),0_2px_8px_-2px_color-mix(in_oklab,#000_12%,transparent)] hover:bg-muted/35 hover:text-foreground active:bg-card active:text-foreground data-[state=open]:bg-card';

/**
 * Shared hover/focus chrome for clickable resource cards (products, reviews, …).
 * Prefer this over freestyle `hover:shadow-md` + border mixes.
 */
export const resourceInteractiveCardClass =
  'transition-[color,background-color,box-shadow,border-color] duration-150 hover:border-[color:var(--ghost-accent-border)] hover:bg-[color:var(--ghost-accent-bg)]/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

/** Brief flash after creating a list item so it is easy to spot. */
export const RESOURCE_JUST_CREATED_HIGHLIGHT_MS = 2200;

export const resourceJustCreatedCardClass =
  'border-[color:var(--ghost-accent-border)] bg-[color:var(--ghost-accent-bg)] ring-2 ring-[color:var(--brand-500)]/30 ring-offset-2 ring-offset-background duration-500 motion-reduce:ring-1';

/** In-list long-press / open-menu chrome. Inset so IonContent cannot clip it. */
export const resourceQuickMenuActiveCardClass =
  'relative z-10 border-primary bg-[color:var(--ghost-accent-bg)] shadow-lg outline outline-2 outline-primary -outline-offset-2';
